/* eslint-disable quotes */
import { parseContent } from "@/utils/parseContent";

export function concatString(string: string, maxLength: number): string {
  // Slice by code points, not UTF-16 units, so multi-byte characters (emoji)
  // are never split into a lone surrogate — which serializes differently on the
  // server (U+FFFD) vs the client and causes hydration mismatches.
  const chars = Array.from(string ?? "");
  return chars.length > maxLength
    ? `${chars.slice(0, maxLength).join("")}...`
    : string;
}
export function convertSnakeToCamelCase(snakeCaseString: string): string {
  return snakeCaseString.replace(/_([a-z])/g, (match, letter) =>
    letter.toUpperCase()
  );
}

export function convertSnakeToCapitalCase(snakeCaseString: string): string {
  return snakeCaseString
    ?.replace(/_/g, " ") // Replace underscores with spaces
    ?.replace(/\b\w/g, (match) => match.toUpperCase()); // Capitalize the first letter of each word
}

/**
 * Encodes special characters to HTML entities.
 * Converts &, <, >, ", and ' to their HTML entity equivalents.
 */
export function encodeHTML(value: string): string {
  return value
    ?.replace(/&/g, "&amp;")
    ?.replace(/>/g, "&gt;")
    ?.replace(/</g, "&lt;")
    ?.replace(/"/g, "&quot;")
    ?.replace(/'/g, "&#039;"); // Handles single quotes as well
}

/**
 * Decodes HTML entities back to their original characters.
 * Converts &amp; to &, &lt; to <, &gt; to >, &quot; to ", and &#039; to '.
 */
export function decodeHTML(value: string): string {
  if (!value) {
    return "";
  }
  return value
    .replace(/&amp;/g, "&")
    .replace(/&#0*38;/g, "&")
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ");
}

/**
 * Convert each word in the Camel case.
 */
export function capitalize(str: string | undefined): string {
  if (!str) {
    return "";
  }

  if (str.length === 2) {
    return str.toUpperCase();
  }

  return str
    .toLowerCase()
    .split(" ")
    .map((word) =>
      word.length === 2
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
}

export function reviewerLastName(
  lastName: string,
  isAuthenticated: boolean | undefined,
  firstName?: string
): string {
  if (isAuthenticated) {
    return `${firstName} ${lastName}`;
  }

  if (!firstName) {
    return `${lastName.charAt(0)}.`;
  }

  const parts = firstName.trim().split(" ");
  if (parts.length > 1) {
    return `${parts[0]} ${parts[1]?.charAt(0)}.`;
  }

  return `${firstName} ${lastName.charAt(0)}.`;
}

export function inclusiveOrganizationContent(
  content: string,
  maxWords = 75
): string {
  // remove <h2> headings
  const cleanedContent = content?.replace(/<h2[^>]*>[\s\S]*?<\/h2>/gi, "");
  const organization_content = parseContent(cleanedContent);

  // strip all HTML tags
  const textOnly = organization_content.replace(/<[^>]+>/g, "");
  const words = textOnly.trim().split(/\s+/);

  if (words.length <= maxWords) {
    // nothing is truncated → return full text, no ellipsis
    return words.join(" ");
  }

  // truncate safely at the last full word
  const truncated = words.slice(0, maxWords).join(" ");
  return `${truncated}…`;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / k ** i).toFixed(0));
  return `${size} ${sizes[i]}`;
}
